import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Star, Loader2, Tag, CheckCircle, X, AlertTriangle, ArrowRight, Info } from 'lucide-react';
import { toast } from 'sonner';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import api from '@/lib/api';
import PayPalTrustBadge from '@/components/PayPalTrustBadge';
import {
  HoverCard, HoverCardTrigger, HoverCardContent,
} from '@/components/ui/hover-card';

// Phase 2: 5-tier matrix. Keys mirror the backend tier identifiers
// (`subscription_tiers.py`). Two free tiers (Open Play, Instructor
// Starter) are render-only — no PayPal checkout.
const TIERS = [
  {
    key: 'open_play',
    name: 'Open Play',
    path: 'player',
    free: true,
    blurb: 'Browse events, post your own (per-event fee), basic profile.',
    features: [
      'View public event listings',
      'Post events ($0.99 free / $9.99 paid)',
      'Basic profile',
    ],
  },
  {
    key: 'verified_player',
    name: 'Verified Player',
    path: 'player',
    monthly: 0.99,
    annual: 9.99,
    blurb: 'Connect with verified players, search by skill, message anyone.',
    features: [
      'Full player search & filters',
      'In-site messaging',
      'Email & SMS alerts',
      'Request games with 1-3 players',
      'View other player profiles',
    ],
  },
  {
    key: 'instructor_starter',
    name: 'Instructor Starter',
    path: 'instructor',
    free: true,
    blurb: 'List yourself as an instructor. Post events at standard rates.',
    features: [
      'Public instructor profile',
      'Post events ($0.99 free / $9.99 paid)',
      'Reach players searching for instructors',
    ],
  },
  {
    key: 'instructor_plus',
    name: 'Instructor Advantage',
    path: 'instructor',
    monthly: 9.99,
    annual: 99.99,
    blurb: 'Everything in Verified Player + free event posting + discounted paid events.',
    features: [
      'All Verified Player benefits',
      'FREE event posting for free events',
      'Discounted paid events ($6.99 vs $9.99)',
      'Instructor profile + featured listing',
    ],
    highlight: true,
  },
  {
    key: 'instructor_unlimited',
    name: 'Instructor Unlimited',
    path: 'instructor',
    monthly: 29.99,
    annual: 299.99,
    blurb: 'Free event posting on ALL events (free and paid). Premium Instructor badge.',
    features: [
      'All Instructor Advantage benefits',
      'FREE event posting (paid events too)',
      'Premium Instructor badge',
      'Priority placement in search',
    ],
  },
];

const FREE_TIER_KEYS = new Set(['open_play', 'instructor_starter']);
// Legacy DB values that still mean "free / no paid plan" until migration runs.
const LEGACY_FREE_TIER_KEYS = new Set(['free', '', null, undefined]);

function tierDisplay(key) {
  const found = TIERS.find((t) => t.key === key);
  return found ? found.name : key;
}

// ── Plan Comparison Tooltip ─────────────────────────────────────────────
// Shows a side-by-side "what changes" view on hover/focus of a Subscribe
// button. Goal: make the cost-of-not-upgrading concrete in the moment.
//
// Inputs:
//   - currentKey : the user's effective tier (one of TIERS.key, or 'open_play'
//                  as fallback when the DB has legacy/free).
//   - targetTier : the TIERS entry the user is hovering.
//   - cadence    : 'monthly' or 'annual' (drives the price diff).
//
// Output: a HoverCard panel with: target price, delta vs current,
// top 3 features gained, and (if downgrading from a higher paid tier)
// the features that will be lost.

// Map any tier key (incl. legacy) into the canonical TIERS index.
function normaliseTierKey(key) {
  const k = (key || '').toLowerCase();
  if (TIERS.find((t) => t.key === k)) return k;
  // Legacy mappings (best-effort while migration hasn't run yet)
  if (k === 'lifetime') return 'instructor_unlimited';
  if (k === 'monthly' || k === 'annual') return 'verified_player';
  return 'open_play';
}

function priceFor(tier, cadence) {
  if (!tier || tier.free) return 0;
  return Number(tier[cadence] || 0);
}

function PlanComparisonHover({ currentKey, targetTier, cadence, children }) {
  const normalisedCurrent = normaliseTierKey(currentKey);
  const currentTier = TIERS.find((t) => t.key === normalisedCurrent) || TIERS[0];
  const targetPrice = priceFor(targetTier, cadence);
  const currentPrice = priceFor(currentTier, cadence);
  const delta = targetPrice - currentPrice;
  const isSameTier = currentTier.key === targetTier.key;
  const isUpgrade = delta > 0 || (currentTier.free && !targetTier.free);
  const isDowngrade = delta < 0 || (!currentTier.free && targetTier.free);

  // Compute features gained / lost (set diff on the canonical features
  // lists). Limited to top 3 each side to keep the panel scannable.
  const cur = new Set(currentTier.features);
  const tgt = new Set(targetTier.features);
  const gained = targetTier.features.filter((f) => !cur.has(f)).slice(0, 3);
  const lost = currentTier.features.filter((f) => !tgt.has(f)).slice(0, 3);

  const deltaLabel = (() => {
    if (isSameTier) return 'Same tier';
    if (currentTier.free && !targetTier.free) return `+$${targetPrice.toFixed(2)} ${cadence === 'monthly' ? '/ month' : '/ year'}`;
    if (!currentTier.free && targetTier.free) return `Save $${Math.abs(currentPrice).toFixed(2)} ${cadence === 'monthly' ? '/ month' : '/ year'}`;
    const sign = delta >= 0 ? '+' : '-';
    return `${sign}$${Math.abs(delta).toFixed(2)} ${cadence === 'monthly' ? '/ month' : '/ year'} vs current`;
  })();

  return (
    <HoverCard openDelay={150} closeDelay={50}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        align="center"
        sideOffset={8}
        className="w-80 p-0 overflow-hidden"
        data-testid={`plan-compare-${targetTier.key}`}
      >
        {/* Header: current → target */}
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
          <div className="flex items-center justify-between text-xs font-medium text-slate-500 uppercase tracking-wide">
            <span data-testid="plan-compare-current">{currentTier.name}</span>
            <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-slate-900" data-testid="plan-compare-target">{targetTier.name}</span>
          </div>
          <div className="mt-1.5 flex items-baseline justify-between">
            <span className="text-xs text-slate-500">{cadence === 'monthly' ? 'Monthly' : 'Annual'}</span>
            <span
              className={`text-sm font-bold ${isUpgrade ? 'text-amber-600' : isDowngrade ? 'text-[#0f766e]' : 'text-slate-700'}`}
              data-testid="plan-compare-delta"
            >
              {deltaLabel}
            </span>
          </div>
        </div>
        {/* What changes */}
        <div className="p-4 space-y-3 text-sm">
          {isSameTier ? (
            <div className="text-xs text-slate-500 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
              You're already on this plan — nothing would change.
            </div>
          ) : (
            <>
              {gained.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#0f766e] mb-1.5">
                    You'd gain
                  </p>
                  <ul className="space-y-1">
                    {gained.map((f) => (
                      <li key={f} className="flex items-start gap-1.5 text-xs text-slate-700">
                        <Check className="w-3 h-3 text-[#56dfb4] flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {lost.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-1.5">
                    You'd lose
                  </p>
                  <ul className="space-y-1">
                    {lost.map((f) => (
                      <li key={f} className="flex items-start gap-1.5 text-xs text-slate-700">
                        <X className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {gained.length === 0 && lost.length === 0 && (
                <p className="text-xs text-slate-500">
                  Different track ({currentTier.path} → {targetTier.path}). Features overlap; check the full list on each card.
                </p>
              )}
            </>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function PayPalCheckoutModal({ tierKey, cadence, paypalConfig, onClose, onSuccess, isSwitching }) {
  if (!paypalConfig) return null;

  const planId = `${tierKey}_${cadence}`;
  const planPaypalId = paypalConfig.plan_ids?.[planId];
  const tierDef = TIERS.find((t) => t.key === tierKey);
  const amount = tierDef?.[cadence] ?? 0;

  const scriptOptions = {
    clientId: paypalConfig.client_id,
    currency: 'USD',
    intent: 'subscription',
    vault: true,
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="paypal-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Subscribe — {tierDef?.name}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-2">
          ${Number(amount).toFixed(2)} / {cadence === 'monthly' ? 'month' : 'year'}
        </p>
        {isSwitching && (
          <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="switching-no-proration-warning">
            <strong>Heads up:</strong> Switching plans is <strong>not prorated</strong>. Your previous
            subscription will be cancelled at PayPal as soon as the new one activates. You may overlap
            briefly — there are no refunds for the unused portion.
          </div>
        )}
        <div className="mb-4">
          <PayPalTrustBadge size="compact" />
        </div>
        {!planPaypalId ? (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="plan-not-configured">
            This plan isn't configured yet. Please contact support.
          </div>
        ) : (
          <PayPalScriptProvider options={scriptOptions}>
            <PayPalButtons
              style={{ layout: 'vertical', shape: 'rect', label: 'subscribe' }}
              forceReRender={[planId]}
              createSubscription={async (_data, actions) => {
                return actions.subscription.create({ plan_id: planPaypalId });
              }}
              onApprove={async (data) => {
                try {
                  await api.post('/paypal/subscriptions/activate', {
                    subscription_id: data.subscriptionID,
                    plan_id: planId,
                  });
                  onSuccess();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Could not activate subscription');
                }
              }}
              onError={(err) => {
                console.error('PayPal error:', err);
                toast.error('PayPal error. Please try again.');
              }}
              onCancel={() => toast.info('Payment canceled')}
            />
          </PayPalScriptProvider>
        )}
      </div>
    </div>
  );
}

export default function SubscriptionPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [checkout, setCheckout] = useState(null); // { tierKey, cadence }
  const [cadence, setCadence] = useState(searchParams.get('cadence') === 'annual' ? 'annual' : 'monthly');
  // Cancel-subscription modal state — mirrors the same flow on the
  // Settings page (both now use a real modal instead of window.confirm
  // so users see billing-cycle expectations before confirming).
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post('/subscription/cancel');
      await refreshUser();
      if (data?.self_healed) {
        toast.success(
          'Cancelled. If you were charged recently, our team has been notified to double-check at PayPal.',
          { duration: 6000 },
        );
      } else {
        toast.success('Subscription cancelled. You keep access until the end of your billing period.');
      }
      setCancelConfirmOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel — please email support if this keeps happening.');
    } finally {
      setCancelling(false);
    }
  };
  // Coupon entry retained as a manual field for future campaigns; the
  // MFMFREE3 auto-apply / pre-fill flow was removed when that promo
  // expired.
  const queryPromo = searchParams.get('promo') || searchParams.get('coupon');
  const queryTier = searchParams.get('tier');
  const [couponCode, setCouponCode] = useState(queryPromo || '');
  const [couponValid, setCouponValid] = useState(null);
  const [couponMessage, setCouponMessage] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(
    TIERS.find((t) => t.key === queryTier && !t.free)?.key || null
  );
  const [planNudge, setPlanNudge] = useState(false);
  const plansRef = useRef(null);
  const couponRef = useRef(null);
  const currentTier = (user?.subscription_tier || 'open_play').toLowerCase();
  const isOnPaidTier = !FREE_TIER_KEYS.has(currentTier) && !LEGACY_FREE_TIER_KEYS.has(currentTier);

  useEffect(() => {
    api.get('/paypal/config').then(({ data }) => setPaypalConfig(data)).catch(() => {});
  }, []);

  const couponPlanIdForSelectedTier = () => {
    if (!selectedTier) return null;
    return `${selectedTier}_${cadence}`;
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    if (!selectedTier) {
      setPlanNudge(true);
      plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    setPlanNudge(false);
    setCouponLoading(true);
    setCouponValid(null);
    setCouponMessage('');
    try {
      const { data } = await api.post('/coupons/validate', { code: couponCode.trim(), plan_id: couponPlanIdForSelectedTier() });
      setCouponValid(true);
      setCouponMessage(data.message);
    } catch (err) {
      setCouponValid(false);
      setCouponMessage(err.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRedeemCoupon = async () => {
    if (!couponValid || !selectedTier) return;
    setRedeemLoading(true);
    try {
      const { data } = await api.post('/coupons/redeem', { code: couponCode.trim(), plan_id: couponPlanIdForSelectedTier() });
      toast.success(data.message || 'Coupon redeemed!');
      await refreshUser();
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to redeem coupon');
    } finally {
      setRedeemLoading(false);
    }
  };

  const handleSubscribeClick = (tierKey) => {
    // Coupon-in-hand guard: route to plan selection + apply step
    if (!isOnPaidTier && couponCode.trim()) {
      setSelectedTier(tierKey);
      setPlanNudge(false);
      setTimeout(() => couponRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
      return;
    }
    if (!paypalConfig) {
      toast.error('Payment system loading — please try again in a moment.');
      return;
    }
    setCheckout({ tierKey, cadence });
  };

  const handleCheckoutSuccess = async () => {
    setCheckout(null);
    toast.success('Payment successful! Your membership is now active.');
    await refreshUser();
    navigate('/payment/success');
  };

  return (
    <div data-testid="subscription-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {isOnPaidTier ? 'Subscription Management' : 'Choose Your Plan'}
        </h1>
        <p className="text-slate-500 mt-2">
          {isOnPaidTier
            ? `You're currently on the ${tierDisplay(currentTier)} plan`
            : 'Connect with verified players, post events, or grow your teaching practice'}
        </p>
      </div>

      {isOnPaidTier && (
        <Card className="mb-8 border-[#56dfb4]/30 bg-[#56dfb4]/5">
          <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#56dfb4]/20 flex items-center justify-center shrink-0">
              <Check className="w-6 h-6 text-[#56dfb4]" />
            </div>
            <div className="flex-1">
              <p className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="current-plan-label">
                {tierDisplay(currentTier)} — Active
              </p>
              <p className="text-sm text-slate-500">You have full access to your tier&apos;s benefits</p>
            </div>
            <Button
              variant="outline"
              onClick={() => setCancelConfirmOpen(true)}
              className="rounded-full text-red-600 border-red-200 hover:bg-red-50 shrink-0"
              data-testid="subscription-cancel-btn"
            >
              Cancel subscription
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Instructor-first framing — nudges instructor accounts to the
          instructor track even when they land on this page from a generic
          referral / Refer & Earn link. Renders for instructor users who
          aren't already on a paid instructor plan. */}
      {user?.is_instructor && !['instructor_plus', 'instructor_unlimited'].includes(currentTier) && (
        <div
          className="mb-6 p-4 rounded-2xl bg-[#b456df]/5 border border-[#b456df]/30 flex items-start gap-3"
          data-testid="instructor-framing-banner"
        >
          <div className="w-10 h-10 rounded-xl bg-[#b456df]/15 flex items-center justify-center flex-shrink-0">
            <span className="text-xl" aria-hidden="true">🎓</span>
          </div>
          <div className="flex-1 text-sm text-slate-700">
            <p className="font-semibold text-[#7d2eaf] mb-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
              You're an instructor — these plans are for you
            </p>
            <p className="text-slate-600">
              <strong>Instructor Advantage</strong> and <strong>Instructor Unlimited</strong> include the Premium Instructor badge, free / discounted event posting, and your public instructor profile. Player plans don't.
            </p>
          </div>
        </div>
      )}

      {/* Player-first framing — symmetric counterpart to the instructor
          banner. Renders for non-instructor users who aren't on a paid
          player tier yet (most often open_play users). Keeps the
          instructor cards from dominating the visual hierarchy for
          players who don't teach. */}
      {!user?.is_instructor && currentTier !== 'verified_player' && (
        <div
          className="mb-6 p-4 rounded-2xl bg-[#56c6df]/5 border border-[#56c6df]/30 flex items-start gap-3"
          data-testid="player-framing-banner"
        >
          <div className="w-10 h-10 rounded-xl bg-[#56c6df]/15 flex items-center justify-center flex-shrink-0">
            <span className="text-xl" aria-hidden="true">👋</span>
          </div>
          <div className="flex-1 text-sm text-slate-700">
            <p className="font-semibold text-[#2b8aa8] mb-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
              You're a player — here are the plans for you
            </p>
            <p className="text-slate-600">
              <strong>Verified Player</strong> unlocks the full player directory, in-site messaging, and event/SMS alerts. Instructor plans below are only useful if you teach Mahjong.
            </p>
          </div>
        </div>
      )}

      {/* Cadence toggle */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex bg-slate-100 p-1 rounded-full" data-testid="cadence-toggle">
          <button
            onClick={() => setCadence('monthly')}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${cadence === 'monthly' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}
            data-testid="cadence-monthly-btn"
          >
            Monthly
          </button>
          <button
            onClick={() => setCadence('annual')}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${cadence === 'annual' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}
            data-testid="cadence-annual-btn"
          >
            Annual <span className="text-xs text-[#56dfb4] ml-1">save up to 17%</span>
          </button>
        </div>
      </div>

      {/* Coupon-in-input guidance banner */}
      {!isOnPaidTier && couponCode.trim() && (
        <div
          className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 flex items-start gap-3"
          data-testid="coupon-guidance-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-amber-900">
            <p className="font-semibold mb-1">You have a coupon code in hand — here's how to redeem it:</p>
            <ol className="list-decimal pl-5 space-y-0.5 text-amber-800">
              <li>Click a paid plan below to <strong>select</strong> it.</li>
              <li>Scroll down and click <strong>Apply</strong> next to the coupon code.</li>
            </ol>
            <p className="mt-2 text-xs text-amber-700">
              Don't click "Subscribe" on a plan card — that goes straight to a real charge.
            </p>
          </div>
        </div>
      )}

      {/* Switching warning */}
      {isOnPaidTier && (
        <div className="mb-6 p-4 rounded-2xl bg-blue-50 border border-blue-200 flex items-start gap-3" data-testid="switching-info-banner">
          <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-blue-900">
            <p className="font-semibold mb-1">Switching plans?</p>
            <p>Switching is <strong>not prorated</strong>. Your old subscription will be cancelled at PayPal as soon as the new one activates. You may briefly overlap — there are no refunds for the unused portion.</p>
          </div>
        </div>
      )}

      <div ref={plansRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {TIERS.map((tier) => {
          const isCurrent = currentTier === tier.key;
          const isSelected = selectedTier === tier.key;
          const price = tier.free ? 0 : tier[cadence];
          const periodSuffix = tier.free ? '' : cadence === 'monthly' ? '/mo' : '/yr';
          return (
            <Card
              key={tier.key}
              className={`relative transition-all duration-300 ${tier.highlight ? 'border-2 border-[#56dfb4]' : 'border-slate-200'} ${isCurrent ? 'ring-2 ring-[#56dfb4]' : ''} ${isSelected && !isCurrent ? 'ring-2 ring-[#56c6df]' : ''} ${!tier.free ? 'cursor-pointer hover:shadow-md' : ''}`}
              onClick={() => !isCurrent && !tier.free && setSelectedTier(tier.key)}
              data-testid={`sub-plan-${tier.key}`}
            >
              {tier.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#56dfb4] text-slate-900 rounded-full text-xs font-bold whitespace-nowrap">
                    <Star className="w-3 h-3" /> Most Popular
                  </span>
                </div>
              )}
              <CardContent className="p-5 pt-7">
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">{tier.path === 'player' ? 'Player' : 'Instructor'}</div>
                <h3 className="font-semibold text-base mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{tier.name}</h3>
                <p className="text-xs text-slate-500 mb-3 min-h-[2.5rem]">{tier.blurb}</p>
                <div className="mb-4">
                  {tier.free ? (
                    <span className="text-2xl font-bold">Free</span>
                  ) : (
                    <>
                      <span className="text-2xl font-bold">${price}</span>
                      <span className="text-slate-500 text-xs ml-1">{periodSuffix}</span>
                    </>
                  )}
                </div>
                <ul className="space-y-1.5 mb-5 text-xs">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-1.5">
                      <Check className="w-3.5 h-3.5 text-[#56dfb4] flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <Button disabled className="w-full rounded-full text-xs" variant="outline" data-testid={`sub-current-${tier.key}`}>Current Plan</Button>
                ) : tier.free ? (
                  <Button disabled className="w-full rounded-full text-xs" variant="outline">
                    {currentTier === tier.key ? 'Current Plan' : 'Free tier (no checkout)'}
                  </Button>
                ) : (!isOnPaidTier && couponCode.trim()) ? (
                  <PlanComparisonHover currentKey={currentTier} targetTier={tier} cadence={cadence}>
                    <Button onClick={(e) => { e.stopPropagation(); handleSubscribeClick(tier.key); }}
                      className={`w-full rounded-full text-xs ${isSelected ? 'bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]' : 'bg-amber-500 text-white hover:bg-amber-600'}`}
                      data-testid={`sub-select-${tier.key}`}>
                      {isSelected ? '✓ Selected — apply code below' : 'Use with my code'}
                    </Button>
                  </PlanComparisonHover>
                ) : (
                  <PlanComparisonHover currentKey={currentTier} targetTier={tier} cadence={cadence}>
                    <Button onClick={(e) => { e.stopPropagation(); handleSubscribeClick(tier.key); }}
                      className={`w-full rounded-full text-xs ${tier.highlight ? 'bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]' : 'bg-[#56c6df] text-white hover:bg-[#3eb0c9]'}`}
                      data-testid={`sub-select-${tier.key}`}>
                      Subscribe
                    </Button>
                  </PlanComparisonHover>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Coupon Code Section */}
      {!isOnPaidTier && (
        <Card className="border-slate-200" ref={couponRef} data-testid="coupon-section">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Tag className="w-5 h-5 text-[#b456df]" />
              <h3 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Have a Promo Code?</h3>
            </div>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label className="text-sm mb-1.5 block">Coupon Code</Label>
                <Input
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponValid(null); setCouponMessage(''); }}
                  placeholder="Enter promo code"
                  className="rounded-xl uppercase"
                  data-testid="coupon-code-input"
                />
              </div>
              <Button
                onClick={handleValidateCoupon}
                disabled={couponLoading || !couponCode.trim() || !selectedTier}
                className="rounded-full bg-[#b456df] text-white hover:bg-[#9a3ec4] px-6"
                data-testid="validate-coupon-btn"
              >
                {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
              </Button>
            </div>
            {!selectedTier && couponCode && (
              <p className={`text-xs mt-2 ${planNudge ? 'text-red-600 font-semibold' : 'text-amber-500'}`} data-testid="coupon-plan-nudge">
                {planNudge
                  ? 'Please select a paid plan above first, then click Apply.'
                  : 'Please select a paid plan above before applying a coupon'}
              </p>
            )}
            {couponMessage && (
              <div className={`mt-3 p-3 rounded-xl text-sm flex items-center gap-2 ${couponValid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`} data-testid="coupon-feedback">
                {couponValid ? <CheckCircle className="w-4 h-4" /> : null}
                {couponMessage}
              </div>
            )}
            {couponValid && (
              <>
                <p className="mt-2 text-xs text-slate-500">This coupon grants you free access for the applicable period. No PayPal checkout needed.</p>
                <Button
                  onClick={handleRedeemCoupon}
                  disabled={redeemLoading}
                  className="mt-3 rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] px-8"
                  data-testid="redeem-coupon-btn"
                >
                  {redeemLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activate Free Access'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {checkout && (
        <PayPalCheckoutModal
          tierKey={checkout.tierKey}
          cadence={checkout.cadence}
          paypalConfig={paypalConfig}
          isSwitching={isOnPaidTier}
          onClose={() => setCheckout(null)}
          onSuccess={handleCheckoutSuccess}
        />
      )}

      {/* Cancel-subscription affirmation modal. Explains the
          keep-access-until-period-end contract so users don't panic
          about losing access immediately + reduces the "I cancelled
          but got charged" support pattern that led to Lauren
          Stemen's 27 Jul 2026 case. */}
      {cancelConfirmOpen && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/70 flex items-start justify-center p-4 overflow-y-auto"
          data-testid="subscription-cancel-modal-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 mt-16"
            data-testid="subscription-cancel-modal"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Cancel subscription?
              </h3>
              <button
                onClick={() => !cancelling && setCancelConfirmOpen(false)}
                className="p-1 hover:bg-slate-100 rounded-full disabled:opacity-50"
                disabled={cancelling}
                aria-label="Close"
                data-testid="subscription-cancel-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              You&apos;ll cancel your <strong>{tierDisplay(currentTier)}</strong> plan.
              You&apos;ll <strong>keep full access</strong> until the end of your current
              billing period, then drop to the free tier in your track. <strong>No further charges</strong>.
            </p>
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 mb-4 text-xs text-amber-900 leading-relaxed">
              If you cancelled directly at PayPal recently and are still seeing charges,
              please email <a href="mailto:meetformahjong@gmail.com" className="underline font-medium">meetformahjong@gmail.com</a>
              — we&apos;ll investigate and refund if appropriate.
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setCancelConfirmOpen(false)}
                disabled={cancelling}
                className="flex-1 rounded-full"
                data-testid="subscription-cancel-modal-keep-btn"
              >
                Never mind
              </Button>
              <Button
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="flex-1 rounded-full bg-red-500 hover:bg-red-600 text-white"
                data-testid="subscription-cancel-modal-confirm-btn"
              >
                {cancelling ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" /> Cancelling…</>) : 'Yes, cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
